import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, Clock, Calendar, GraduationCap, CheckCircle2, Briefcase, Euro, Accessibility, Target, BookOpen, Newspaper, ExternalLink, Award, ShieldCheck, BarChart } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/Accordion';
import { FORMATIONS, LINKS } from '../data/constants';
import { SocialProof } from '../components/SocialProof';
import { Cap360 } from '../components/Cap360';
export function FormationDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const formation = FORMATIONS.find(f => f.id === id);
  if (!formation) {
    return <Navigate to="/formations" replace data-id="element-1286" />;
  }
  return <div className="flex flex-col w-full pb-24 bg-muted" data-id="element-1287">
      {/* Custom Header for Formation */}
      <div className="relative bg-lavande py-16 md:py-24 overflow-hidden" data-id="element-1288">
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[500px] h-[500px] bg-primary rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1289" />
        <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/4 w-[300px] h-[300px] bg-brand-orange rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1290" />

        <div className="container max-w-6xl mx-auto relative z-10" data-id="element-1291">
          <div className="flex flex-wrap gap-3 mb-8" data-id="element-1292">
            <Badge className="bg-primary text-white text-sm px-4 py-1.5 font-bold border-none" data-id="element-1293">
              {formation.level}
            </Badge>
            <Badge variant="outline" className="bg-white/50 border-primary/20 text-primary-dark text-sm px-4 py-1.5 font-semibold" data-id="element-1294">
              {formation.rncp}
            </Badge>
            <Badge variant="secondary" className="bg-brand-orange text-white text-sm px-4 py-1.5 font-bold border-none" data-id="element-1295">
              Session {formation.session}
            </Badge>
          </div>

          <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-primary-dark mb-6 tracking-tight leading-[1.1]" data-id="element-1296">
            {formation.title}
          </h1>

          <p className="text-xl md:text-2xl text-primary-dark/80 max-w-3xl leading-relaxed mb-10 font-medium" data-id="element-1297">
            {formation.description}
          </p>

          <div className="flex flex-wrap gap-8 text-base font-medium text-primary-dark bg-white p-8 rounded-2xl shadow-xl inline-flex border-none" data-id="element-1298">
            <div className="flex items-center gap-4 group" data-id="element-1299">
              <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1300">
                <Clock className="w-6 h-6 text-brand-orange animate-soft-bounce" data-id="element-1301" />
              </div>
              <div data-id="element-1302">
                <span className="block text-sm text-muted-foreground mb-1" data-id="element-1303">
                  Durée
                </span>
                <span className="font-bold" data-id="element-1304">{formation.duration}</span>
              </div>
            </div>
            <div className="w-px bg-slate-100 hidden sm:block" data-id="element-1305"></div>
            <div className="flex items-center gap-4 group" data-id="element-1306">
              <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1307">
                <Calendar className="w-6 h-6 text-brand-orange animate-soft-bounce" data-id="element-1308" />
              </div>
              <div data-id="element-1309">
                <span className="block text-sm text-muted-foreground mb-1" data-id="element-1310">
                  Rythme
                </span>
                <span className="font-bold" data-id="element-1311">{formation.rhythm}</span>
              </div>
            </div>
            <div className="w-px bg-slate-100 hidden sm:block" data-id="element-1312"></div>
            <div className="flex items-center gap-4 group" data-id="element-1313">
              <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1314">
                <GraduationCap className="w-6 h-6 text-brand-orange animate-soft-bounce" data-id="element-1315" />
              </div>
              <div data-id="element-1316">
                <span className="block text-sm text-muted-foreground mb-1" data-id="element-1317">
                  Rentrée
                </span>
                <span className="font-bold" data-id="element-1318">{formation.rentree}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container max-w-6xl mx-auto mt-16" data-id="element-1319">
        <div className="grid lg:grid-cols-3 gap-12" data-id="element-1320">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-1321">
            <Tabs defaultValue="programme" className="w-full" data-id="element-1322">
              <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 mb-8 h-auto p-1" data-id="element-1323">
                <TabsTrigger value="programme" className="py-3 text-sm md:text-base" data-id="element-1324">
                  Programme
                </TabsTrigger>
                <TabsTrigger value="modalites" className="py-3 text-sm md:text-base" data-id="element-1325">
                  Modalités
                </TabsTrigger>
                <TabsTrigger value="debouches" className="py-3 text-sm md:text-base" data-id="element-1326">
                  Débouchés
                </TabsTrigger>
                <TabsTrigger value="qualite" className="py-3 text-sm md:text-base" data-id="element-1327">
                  Indicateurs
                </TabsTrigger>
              </TabsList>

              {/* TAB: Programme */}
              <TabsContent value="programme" className="space-y-10 animate-in fade-in-50 duration-500" data-id="element-1328">
                <section data-id="element-1329">
                  <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-2" data-id="element-1330">
                    <Target className="w-6 h-6 text-primary" data-id="element-1331" />
                    Objectifs pédagogiques mesurables
                  </h2>
                  <div className="bg-slate-50 p-6 rounded-lg border border-slate-100" data-id="element-1332">
                    <p className="text-muted-foreground leading-relaxed" data-id="element-1333">
                      {formation.objectives}
                    </p>
                  </div>
                </section>

                <section data-id="element-1334">
                  <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-2" data-id="element-1335">
                    <BookOpen className="w-6 h-6 text-primary" data-id="element-1336" />
                    Blocs de compétences
                  </h2>
                  <Accordion type="single" collapsible className="w-full" data-id="element-1337">
                    {formation.certification.blocs.map((bloc, i) => <AccordionItem key={i} value={`bloc-${i}`} data-id="element-1338">
                        <AccordionTrigger className="text-left font-semibold hover:text-primary" data-id="element-1339">
                          {bloc.code}
                        </AccordionTrigger>
                        <AccordionContent data-id="element-1340">
                          <p className="text-muted-foreground mt-2" data-id="element-1341">
                            {bloc.description}
                          </p>
                        </AccordionContent>
                      </AccordionItem>)}
                  </Accordion>
                </section>
              </TabsContent>

              {/* TAB: Modalités */}
              <TabsContent value="modalites" className="space-y-10 animate-in fade-in-50 duration-500" data-id="element-1342">
                <section className="space-y-6" data-id="element-1343">
                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-1344">
                    <div data-id="element-1345">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1346">Public visé</h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1347">
                        {formation.public}
                      </p>
                    </div>
                    <div data-id="element-1348">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1349">Prérequis</h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1350">
                        {formation.prerequisites}
                      </p>
                    </div>
                  </div>
                  <div data-id="element-1351">
                    <h3 className="text-lg font-bold mb-2" data-id="element-1352">
                      Méthodes mobilisées
                    </h3>
                    <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-1353">
                      {formation.methods}
                    </p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-1354">
                    <div data-id="element-1355">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1356">
                        Évaluations en formation
                      </h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1357">
                        {formation.evaluation}
                      </p>
                    </div>
                    <div data-id="element-1358">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1359">
                        Épreuve certificative
                      </h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1360">
                        {formation.epreuve}
                      </p>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-1361">
                    <div data-id="element-1362">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1363">Accès</h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1364">
                        {formation.acces}
                      </p>
                    </div>
                    <div data-id="element-1365">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1366">Délai d'accès</h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1367">
                        {formation.delaiAcces}
                      </p>
                    </div>
                  </div>
                </section>
              </TabsContent>

              {/* TAB: Débouchés */}
              <TabsContent value="debouches" className="space-y-10 animate-in fade-in-50 duration-500" data-id="element-1368">
                <section data-id="element-1369">
                  <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-2" data-id="element-1370">
                    <Briefcase className="w-6 h-6 text-primary" data-id="element-1371" />
                    Débouchés officiels
                  </h2>
                  <div className="flex flex-wrap gap-3 mb-6" data-id="element-1372">
                    {formation.opportunities.map((job, i) => <Badge key={i} variant="secondary" className="text-sm px-4 py-2" data-id="element-1373">
                        {job}
                      </Badge>)}
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 mb-6" data-id="element-1374">
                    <span className="font-semibold text-sm block mb-2" data-id="element-1375">
                      Codes ROME correspondants :
                    </span>
                    <div className="flex flex-wrap gap-2" data-id="element-1376">
                      {formation.romeCodes.map((code, i) => <Badge key={i} variant="outline" className="bg-white" data-id="element-1377">
                          {code}
                        </Badge>)}
                    </div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-1378">
                    <span className="font-semibold text-sm block mb-2" data-id="element-1379">
                      Secteurs :
                    </span>
                    <p className="text-muted-foreground" data-id="element-1380">
                      {formation.secteurs}
                    </p>
                  </div>
                </section>

                <section data-id="element-1381">
                  <h2 className="text-2xl font-bold font-heading mb-4 flex items-center gap-2" data-id="element-1382">
                    <GraduationCap className="w-6 h-6 text-primary" data-id="element-1383" />
                    Poursuites d'études
                  </h2>
                  <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-1384">
                    {formation.furtherStudies}
                  </p>
                </section>
              </TabsContent>

              {/* TAB: Qualité */}
              <TabsContent value="qualite" className="space-y-10 animate-in fade-in-50 duration-500" data-id="element-1385">
                <section data-id="element-1386">
                  <h2 className="text-2xl font-bold font-heading mb-6" data-id="element-1387">
                    Indicateurs de résultats
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1388">
                    {formation.indicators.map((ind, i) => <Card key={i} className="bg-primary/5 border-none" data-id="element-1389">
                        <CardContent className="p-6 text-center flex flex-col justify-center h-full" data-id="element-1390">
                          <div className="text-3xl font-bold text-primary mb-2 font-heading" data-id="element-1391">
                            {ind.label}
                          </div>
                          <div className="text-sm font-medium text-foreground" data-id="element-1392">
                            {ind.value}
                          </div>
                        </CardContent>
                      </Card>)}
                  </div>
                </section>

                {formation.inserJeunes && <section data-id="element-1393">
                    <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-2" data-id="element-1394">
                      <BarChart className="w-6 h-6 text-primary" data-id="element-1395" />
                      Indicateurs nationaux (InserJeunes)
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-6" data-id="element-1396">
                      {/* Emploi */}
                      <Card className="border-none bg-slate-50" data-id="element-1397">
                        <CardContent className="p-6" data-id="element-1398">
                          <div className="text-4xl font-extrabold text-brand-orange mb-2" data-id="element-1399">
                            {formation.inserJeunes.emploi6Mois}
                          </div>
                          <p className="font-medium text-primary-dark" data-id="element-1400">
                            apprenants sortants sont en emploi salarié 6 mois
                            après leur sortie de formation
                          </p>
                        </CardContent>
                      </Card>

                      {/* Poursuite vs Sortie */}
                      <Card className="border-none bg-slate-50" data-id="element-1401">
                        <CardContent className="p-6" data-id="element-1402">
                          <div className="flex justify-between mb-3 text-sm" data-id="element-1403">
                            <span className="font-bold text-primary-dark" data-id="element-1404">
                              {formation.inserJeunes.poursuiteEtudes}{' '}
                              poursuivent
                            </span>
                            <span className="font-bold text-primary-dark" data-id="element-1405">
                              {formation.inserJeunes.sortieFormation} sortent
                            </span>
                          </div>
                          <div className="h-4 w-full bg-slate-200 rounded-full overflow-hidden flex" data-id="element-1406">
                            <div className="h-full bg-primary" style={{
                          width: formation.inserJeunes.poursuiteEtudes
                        }} data-id="element-1407"></div>
                            <div className="h-full bg-brand-orange" style={{
                          width: formation.inserJeunes.sortieFormation
                        }} data-id="element-1408"></div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Rémunération */}
                      <Card className="border-none bg-slate-50" data-id="element-1409">
                        <CardContent className="p-6" data-id="element-1410">
                          <h3 className="font-bold text-primary-dark mb-4" data-id="element-1411">
                            Rémunération 1 an après
                          </h3>
                          <div className="text-center" data-id="element-1412">
                            <div className="text-2xl font-extrabold text-primary mb-1" data-id="element-1413">
                              Entre {formation.inserJeunes.remuneration}
                            </div>
                            <p className="text-sm text-muted-foreground" data-id="element-1414">
                              pour 50% des salariés
                            </p>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Rupture */}
                      <Card className="border-none bg-slate-50" data-id="element-1415">
                        <CardContent className="p-6 flex flex-col justify-center" data-id="element-1416">
                          <div className="text-3xl font-extrabold text-primary-dark mb-2" data-id="element-1417">
                            {formation.inserJeunes.rupture}
                          </div>
                          <p className="font-medium text-primary-dark" data-id="element-1418">
                            Part des contrats interrompus avant leur terme
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                    <p className="text-xs text-muted-foreground mt-4" data-id="element-1419">
                      Source : InserJeunes (Ministère de l'Éducation nationale
                      et de la Jeunesse). Moyennes nationales à titre indicatif.
                    </p>
                  </section>}

                <section data-id="element-1420">
                  <h2 className="text-2xl font-bold font-heading mb-6" data-id="element-1421">
                    Repères RNCP
                  </h2>
                  <Card className="border-none bg-slate-50" data-id="element-1422">
                    <CardContent className="p-6 space-y-4" data-id="element-1423">
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1424">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1425">
                          Intitulé exact
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1426">
                          {formation.certification.intitule}
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1427">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1428">
                          Niveau / Domaine
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1429">
                          {formation.certification.niveauDomaine}
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1430">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1431">
                          Certificateur
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1432">
                          {formation.certification.certificateur}
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1433">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1434">
                          Enregistrement
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1435">
                          {formation.certification.enregistrement}
                        </div>
                      </div>
                      {formation.certification.derniereDelivrance && <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1436">
                          <div className="font-bold text-sm text-muted-foreground" data-id="element-1437">
                            Dernière délivrance
                          </div>
                          <div className="sm:col-span-2 font-medium" data-id="element-1438">
                            {formation.certification.derniereDelivrance}
                          </div>
                        </div>}
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1439">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1440">
                          Capitalisation
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1441">
                          {formation.certification.capitalisation}
                        </div>
                      </div>
                      {formation.certification.correspondance && <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1442">
                          <div className="font-bold text-sm text-muted-foreground" data-id="element-1443">
                            Correspondance
                          </div>
                          <div className="sm:col-span-2 font-medium" data-id="element-1444">
                            {formation.certification.correspondance}
                          </div>
                        </div>}
                      <div className="grid sm:grid-cols-3 gap-4" data-id="element-1445">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1446">
                          Lien officiel
                        </div>
                        <div className="sm:col-span-2 font-medium break-all" data-id="element-1447">
                          <a href={`https://${formation.certification.lien}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-id="element-1448">
                            {formation.certification.lien}
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </section>
              </TabsContent>
            </Tabs>

            {/* Points forts de la formation */}
            <Card className="border-none shadow-lg bg-white overflow-hidden" data-id="element-1449">
              <div className="h-2 w-full bg-brand-orange" data-id="element-1450" />
              <CardHeader className="pt-8" data-id="element-1451">
                <CardTitle className="text-2xl font-extrabold font-heading text-primary-dark flex items-center gap-2" data-id="element-1452">
                  <Target className="w-6 h-6 text-brand-orange" data-id="element-1453" />
                  Les métiers visés
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8 pt-2" data-id="element-1454">
                <p className="text-muted-foreground leading-relaxed mb-6" data-id="element-1455">
                  À l'issue de cette formation, vous pourrez exercer sur de
                  nombreux postes recherchés par les entreprises :
                </p>
                <div className="grid sm:grid-cols-2 gap-3" data-id="element-1456">
                  {formation.opportunities.map((job, i) => <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3 border border-slate-100" data-id="element-1457">
                      <CheckCircle2 className="w-5 h-5 text-primary shrink-0" data-id="element-1458" />
                      <span className="font-medium text-primary-dark text-sm" data-id="element-1459">
                        {job}
                      </span>
                    </div>)}
                </div>
                <div className="mt-8 pt-6 border-t border-slate-100" data-id="element-1460">
                  <Button className="w-full group bg-brand-orange hover:bg-[#E67E22] text-white text-lg py-6 shadow-md" asChild data-id="element-1461">
                    <Link to="/candidater" data-id="element-1462">
                      Je postule à cette formation
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1463" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* CAP 360 — Accompagnement recherche d'alternance */}
            <Cap360 data-id="element-1464" />
          </div>

          {/* Sidebar / Sticky Actions */}
          <div className="lg:col-span-1" data-id="element-1465">
            <div className="sticky top-28 space-y-8" data-id="element-1466">
              <Card className="border-none shadow-xl bg-white overflow-hidden" data-id="element-1467">
                <div className="h-2 w-full bg-gradient-to-r from-primary to-brand-orange" data-id="element-1468" />
                <CardHeader className="bg-white pb-6 pt-8" data-id="element-1469">
                  <CardTitle className="text-2xl font-extrabold font-heading text-center text-primary-dark" data-id="element-1470">
                    Intéressé(e) par cette formation ?
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8 pt-0 space-y-4" data-id="element-1471">
                  <Button className="w-full text-lg py-7 group bg-brand-orange hover:bg-[#E67E22] text-white shadow-md" asChild data-id="element-1472">
                    <Link to="/candidater" data-id="element-1473">
                      Je candidate maintenant
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1474" />
                    </Link>
                  </Button>
                  <Button variant="outline" className="w-full py-6 border-primary/20 text-primary-dark hover:bg-primary/5 font-semibold" asChild data-id="element-1475">
                    <Link to="/catalogue" data-id="element-1476">
                      <BookOpen className="w-5 h-5 mr-2" data-id="element-1477" />
                      Voir le catalogue de formation
                    </Link>
                  </Button>
                  <Button variant="outline" className="w-full py-6 border-primary/20 text-primary-dark hover:bg-primary/5 font-semibold" asChild data-id="element-1478">
                    <Link to="/qualite#indicateurs" data-id="element-1479">
                      <Target className="w-5 h-5 mr-2" data-id="element-1480" />
                      Voir nos indicateurs de résultats
                    </Link>
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg bg-white" data-id="element-1481">
                <CardContent className="p-8 space-y-8" data-id="element-1482">
                  <div className="group" data-id="element-1483">
                    <div className="flex items-center gap-3 font-extrabold text-lg text-primary-dark mb-3" data-id="element-1484">
                      <div className="w-10 h-10 rounded-lg bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1485">
                        <Euro className="w-5 h-5 text-brand-orange animate-soft-pulse" data-id="element-1486" />
                      </div>
                      Tarif et financement
                    </div>
                    <p className="text-muted-foreground leading-relaxed pl-13" data-id="element-1487">
                      {formation.price}
                    </p>
                  </div>

                  <div className="pt-8 border-t border-slate-100 group" data-id="element-1488">
                    <div className="flex items-center gap-3 font-extrabold text-lg text-primary-dark mb-3" data-id="element-1489">
                      <div className="w-10 h-10 rounded-lg bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1490">
                        <Accessibility className="w-5 h-5 text-brand-orange animate-soft-pulse" data-id="element-1491" />
                      </div>
                      Accessibilité handicap
                    </div>
                    <p className="text-muted-foreground leading-relaxed pl-13 mb-4" data-id="element-1492">
                      {formation.handicap}
                    </p>
                    <div className="pl-13" data-id="element-1493">
                      <Button variant="outline" size="sm" className="border-primary/20 text-primary-dark hover:bg-primary/5" asChild data-id="element-1494">
                        <Link to="/accessibilite" data-id="element-1495">
                          En savoir plus
                          <ArrowRight className="w-4 h-4 ml-2" data-id="element-1496" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Réussite & Centre d'examen — TP Conseiller de Vente */}
      {formation.id === 'tp-conseiller-vente' && <section className="py-20 bg-muted" data-id="element-1497">
          <div className="container max-w-5xl mx-auto space-y-10" data-id="element-1498">
            {/* 100% de réussite */}
            <div className="bg-primary-dark text-white rounded-3xl shadow-xl overflow-hidden relative" data-id="element-1499">
              <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/4 w-[400px] h-[400px] bg-brand-orange rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-1500" />
              <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 p-10 md:p-12" data-id="element-1501">
                <div className="shrink-0 text-center" data-id="element-1502">
                  <div className="text-6xl md:text-7xl font-extrabold font-heading text-brand-orange leading-none" data-id="element-1503">
                    100%
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-3 text-lavande font-bold uppercase tracking-wider text-sm" data-id="element-1504">
                    <Award className="w-5 h-5" data-id="element-1505" />
                    de réussite
                  </div>
                </div>
                <div className="w-px h-24 bg-white/10 hidden md:block" data-id="element-1506" />
                <div className="text-center md:text-left" data-id="element-1507">
                  <h2 className="font-heading text-2xl md:text-3xl font-extrabold mb-3" data-id="element-1508">
                    Session d'examen 2025 : 100 % de réussite
                  </h2>
                  <p className="text-lavande text-lg leading-relaxed" data-id="element-1509">
                    L'intégralité de nos candidats au TP Conseiller de Vente a
                    obtenu sa certification lors de la session d'examen 2025. Un
                    résultat qui récompense l'accompagnement individualisé et la
                    pédagogie par l'action de Zillenium Alternance.
                  </p>
                </div>
              </div>
            </div>

            {/* Centre d'examen agréé */}
            <div className="bg-white rounded-3xl shadow-lg border border-slate-100 overflow-hidden" data-id="element-1510">
              <div className="h-2 w-full bg-primary" data-id="element-1511" />
              <div className="p-8 md:p-12" data-id="element-1512">
                <div className="flex items-start gap-5 mb-6" data-id="element-1513">
                  <div className="w-14 h-14 rounded-2xl bg-lavande flex items-center justify-center shrink-0" data-id="element-1514">
                    <ShieldCheck className="w-7 h-7 text-primary" data-id="element-1515" />
                  </div>
                  <div data-id="element-1516">
                    <span className="inline-block bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-2" data-id="element-1517">
                      Centre d'examen agréé
                    </span>
                    <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-primary-dark" data-id="element-1518">
                      Zillenium, centre d'examen du titre professionnel
                    </h2>
                  </div>
                </div>

                <p className="text-muted-foreground leading-relaxed mb-8" data-id="element-1519">
                  Conformément à l'article L. 335-6 du code de l'éducation, le
                  titre professionnel est une certification délivrée au nom du
                  ministre chargé de l'emploi qui atteste de compétences
                  directement opérationnelles en entreprise. La délivrance de
                  cette certification s'appuie sur le respect des modalités
                  d'examen fixées réglementairement. Dans ce cadre, Zillenium
                  veille à ce que le responsable de session ainsi que les
                  membres de jury habilités bénéficient d'une information
                  complète et actualisée des modalités d'examen décrites dans le
                  référentiel de certification.
                </p>

                <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6" data-id="element-1520">
                  <h3 className="font-bold text-primary-dark mb-2" data-id="element-1521">
                    Décision de renouvellement d'agrément n° 0350 — 7 mai 2026
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4" data-id="element-1522">
                    Agrément initialement délivré le 13 mai 2025, renouvelé par
                    cette décision pour la période en cours.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm" data-id="element-1523">
                    <div data-id="element-1524">
                      <span className="text-muted-foreground block mb-1" data-id="element-1525">
                        Autorité
                      </span>
                      <span className="font-medium text-primary-dark" data-id="element-1526">
                        Préfète de la région Bourgogne-Franche-Comté (DREETS)
                      </span>
                    </div>
                    <div data-id="element-1527">
                      <span className="text-muted-foreground block mb-1" data-id="element-1528">
                        Validité totale de l'agrément
                      </span>
                      <span className="font-medium text-primary-dark" data-id="element-1529">
                        Du 13 mai 2025 au 12 mai 2027
                      </span>
                    </div>
                    <div data-id="element-1530">
                      <span className="text-muted-foreground block mb-1" data-id="element-1531">
                        Lieu des sessions d'examen
                      </span>
                      <span className="font-medium text-primary-dark" data-id="element-1532">
                        25 rue Gabriel Cortel, 89300 Joigny
                      </span>
                    </div>
                    <div data-id="element-1533">
                      <span className="text-muted-foreground block mb-1" data-id="element-1534">
                        Capacité par session
                      </span>
                      <span className="font-medium text-primary-dark" data-id="element-1535">
                        Jusqu'à 30 candidats
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>}

      {/* Dans la presse — TP Conseiller de Vente */}
      {formation.id === 'tp-conseiller-vente' && <section className="py-20 bg-white" data-id="element-1536">
          <div className="container max-w-4xl mx-auto" data-id="element-1537">
            <div className="text-center mb-10" data-id="element-1538">
              <div className="inline-flex items-center justify-center p-3 bg-lavande rounded-2xl mb-4" data-id="element-1539">
                <Newspaper className="w-7 h-7 text-primary" data-id="element-1540" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-primary-dark" data-id="element-1541">
                Dans la presse
              </h2>
            </div>
            <a href="https://www.lyonne.fr/joigny-89300/actualites/il-y-a-un-manque-sur-joigny-des-etudiants-mobilises-pour-sensibiliser-au-don-du-sang-dans-la-galerie-marchande_14961315/" target="_blank" rel="noopener noreferrer" className="group block bg-white rounded-3xl shadow-lg border border-slate-100 overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-1542">
              <div className="flex flex-col sm:flex-row" data-id="element-1543">
                <div className="sm:w-48 shrink-0 bg-primary-dark flex flex-col items-center justify-center p-8 text-center" data-id="element-1544">
                  <Newspaper className="w-10 h-10 text-lavande mb-3" data-id="element-1545" />
                  <span className="text-white font-bold font-heading leading-tight" data-id="element-1546">
                    L'Yonne Républicaine
                  </span>
                  <span className="text-lavande text-sm mt-1" data-id="element-1547">lyonne.fr</span>
                </div>
                <div className="p-8 flex-1" data-id="element-1548">
                  <span className="inline-block bg-brand-orange/10 text-brand-orange text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4" data-id="element-1549">
                    Engagement étudiant • Joigny
                  </span>
                  <h3 className="text-xl font-extrabold font-heading text-primary-dark mb-3 leading-snug group-hover:text-primary transition-colors" data-id="element-1550">
                    « Il y a un manque sur Joigny » : des étudiants mobilisés
                    pour sensibiliser au don du sang dans la galerie marchande
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-4" data-id="element-1551">
                    Nos alternants s'engagent sur le terrain au service d'une
                    cause solidaire — une illustration concrète de la pédagogie
                    par l'action chez Zillenium Alternance.
                  </p>
                  <span className="inline-flex items-center text-primary font-bold group-hover:gap-2 transition-all" data-id="element-1552">
                    Lire l'article
                    <ExternalLink className="w-4 h-4 ml-1" data-id="element-1553" />
                  </span>
                </div>
              </div>
            </a>
          </div>
        </section>}

      {/* Témoignages d'alternants — TP Conseiller de Vente */}
      {formation.id === 'tp-conseiller-vente' && <>
          <div className="mt-24" data-id="element-1554">
            <SocialProof title="On les accompagne dans leurs projets de marque" subtitle="Au-delà du diplôme, nos conseillers de vente créent, lancent et défendent de vrais projets de création de marque." testimonials={[{
          url: 'https://www.instagram.com/reel/DXKberwjoL6/embed',
          name: 'Projet de création de marque',
          role: 'TP Conseiller de Vente'
        }, {
          url: 'https://www.instagram.com/p/DXNAq0yjk7y/embed',
          name: 'Projet de création de marque',
          role: 'TP Conseiller de Vente'
        }]} featuredIndex={0} data-id="element-1555" />
          </div>

          {/* CTA après témoignages */}
          <section className="py-20 bg-primary-dark text-white relative overflow-hidden" data-id="element-1556">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary rounded-full blur-[120px] opacity-40 pointer-events-none" data-id="element-1557" />
            <div className="container max-w-3xl mx-auto text-center relative z-10" data-id="element-1558">
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold mb-6 tracking-tight" data-id="element-1559">
                Envie de vivre l'aventure ?
              </h2>
              <p className="text-lavande text-lg md:text-xl mb-10 leading-relaxed font-medium" data-id="element-1560">
                Rejoignez le TP Conseiller de Vente et donnez vie à vos projets
                commerciaux dès la rentrée.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1561">
                <Button size="lg" className="w-full sm:w-auto group bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-10 py-7 shadow-xl" asChild data-id="element-1562">
                  <Link to="/candidater" data-id="element-1563">
                    Je candidate maintenant
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1564" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="w-full sm:w-auto bg-transparent border-white/40 text-white hover:bg-white hover:text-primary-dark text-lg px-8 py-7" asChild data-id="element-1565">
                  <Link to="/entreprises" data-id="element-1566">Je suis une entreprise</Link>
                </Button>
              </div>
            </div>
          </section>
        </>}

      {/* Témoignages d'alternants — BTS NDRC uniquement */}
      {formation.id === 'bts-ndrc' && <>
          <div className="mt-24" data-id="element-1567">
            <SocialProof data-id="element-1568" />
          </div>

          {/* CTA après témoignages */}
          <section className="py-20 bg-primary-dark text-white relative overflow-hidden" data-id="element-1569">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary rounded-full blur-[120px] opacity-40 pointer-events-none" data-id="element-1570" />
            <div className="container max-w-3xl mx-auto text-center relative z-10" data-id="element-1571">
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold mb-6 tracking-tight" data-id="element-1572">
                Convaincu(e) ? Rejoignez la prochaine promotion
              </h2>
              <p className="text-lavande text-lg md:text-xl mb-10 leading-relaxed font-medium" data-id="element-1573">
                Lancez votre candidature dès aujourd'hui et construisez votre
                avenir dans la relation client.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1574">
                <Button size="lg" className="w-full sm:w-auto group bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-10 py-7 shadow-xl" asChild data-id="element-1575">
                  <Link to="/candidater" data-id="element-1576">
                    Je candidate maintenant
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1577" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="w-full sm:w-auto bg-transparent border-white/40 text-white hover:bg-white hover:text-primary-dark text-lg px-8 py-7" asChild data-id="element-1578">
                  <Link to="/entreprises" data-id="element-1579">Je suis une entreprise</Link>
                </Button>
              </div>
            </div>
          </section>
        </>}
    </div>;
}